import { ReportApiDal } from './reportApiDal';
import { AdminApiDal } from '../adminApiDal';
import { inject, injectable } from '../../../../modules/common/di';
import { GetAvailableReportsApiResponse, ReportDeliveryType } from './getAvailableReportsApiResponse';
import { ResponseType } from 'axios';
import { GetReportExecutionsApiResponse } from './getReportExecutionsApiResponse';
import { GetReportGroupsApiResponse } from './getReportGroupsApiResponse';
import { ApiResponseBase } from '../../../models/apiResponseBase';
import { GetSystemStatusApiResponse } from './getSystemStatusApiResponse';
import { GetReportCollectionsApiResponse } from './getReportCollectionsApiResponse';
import { CreateReportCollectionApiResponse, CreateReportCollectionModel } from './createReportCollectionApiResponse';
import { UpdateReportCollectionApiResponse, UpdateReportCollectionModel } from './updateReportCollectionApiResponse';
import { ReportResponseFormat } from './getReportDataApiResponse';

/**
 * Organization Reports API.
 * See {@link https://iotadmins.docs.apiary.io/#reference/reports}
 *
 * The IoT Software Suite provides online reports for different purposes.
 */
@injectable('ReportsAdminApi')
export class ReportsAdminApi {
  @inject('ReportApiDal') protected readonly dal!: ReportApiDal;
  @inject('AdminApiDal') protected readonly adminDal!: AdminApiDal;

  /**
   * Get report groups.
   * See {@link https://iotadmins.docs.apiary.io/#reference/reports/report-groups/get-groups}
   *
   * @param {number} organizationId Mandatory organization ID.
   * @param params Requested parameters.
   * @param {number} [params.analytic] Return only groups, which inlude analytic reports.
   * @param {boolean} [params.all] Return all groups including not assigned to the organization (default is true).
   * @returns {Promise<GetReportGroupsApiResponse>}
   */
  getReportGroups(organizationId: number, params?: {
    analytic?: boolean;
    all?: boolean;
  }): Promise<GetReportGroupsApiResponse> {
    return this.dal.get(`groups/${encodeURIComponent(organizationId.toString())}`, {
      params: params,
    });
  }

  /**
   * Enable report group for organization.
   * See {@link https://iotadmins.docs.apiary.io/#reference/reports/report-groups/put-group-organization-status}
   *
   * @param {number} organizationId Mandatory organization ID.
   * @param params Requested parameters.
   * @param {number} params.reportGroupId Mandatory reports group ID.
   * @param {number} [params.notificationGroupId] Organization notification group. Set it to 0 to clear category.
   * @returns {Promise<ApiResponseBase>}
   */
  setReportGroup(
    organizationId: number,
    params: {
      reportGroupId: number;
      notificationGroupId?: number;
    },
  ): Promise<ApiResponseBase> {
    return this.dal.put(`groups/${encodeURIComponent(organizationId.toString())}`, void 0, {
      params: params,
    });
  }

  /**
   * Disable report group for organization.
   * See {@link https://sboxall.peoplepowerco.com/cloud/apidocs/admin.html#tag/Reports/operation/Delete%20Report%20Group%20Organization}
   *
   * @param {number} organizationId Mandatory organization ID.
   * @param params Requested parameters.
   * @param {number} params.reportGroupId Mandatory reports group ID.
   * @param {number} [params.notificationGroupId] Organization notification group.
   * @returns {Promise<ApiResponseBase>}
   */
  deleteReportGroup(
    organizationId: number,
    params: {
      reportGroupId: number;
    },
  ): Promise<ApiResponseBase> {
    return this.dal.delete(`groups/${encodeURIComponent(organizationId.toString())}`, {
      params: params,
    });
  }

  /**
   * This API provide information about available reports.
   * See {@link https://iotadmins.docs.apiary.io/#reference/reports/available-reports/get-reports}
   *
   * @param [params] Requested parameters.
   * @param {number} [params.reportId] Optional filter by report ID.
   * @param {number} [params.reportGroupId] Optional filter by report group ID.
   * @param {number} [params.organizationId] Optional filter by organization ID.
   * @param {boolean} [params.analytic] Optional filter by 'analytic' report flag.
   * @returns {Promise<GetAvailableReportsApiResponse>}
   */
  getAvailableReports(params?: {
    reportId?: number;
    reportGroupId?: number;
    organizationId?: number;
    analytic?: boolean;
  }): Promise<GetAvailableReportsApiResponse> {
    return this.dal.get('reports', {
      params: params,
    });
  }

  /**
   * Generate specific report.
   * See {@link https://iotadmins.docs.apiary.io/#reference/reports/generate-report/generate-report}
   *
   * The result can provided in CSV or HTML format. Long generated reports can be delivered by email.
   * All report parameters should be provided in the query string.
   *
   * @param params Requested parameters.
   * @param {number} params.reportId Report ID.
   * @param {ReportDeliveryType} params.deliveryType Way to return report data:
   *  2 - by email
   *  3 - save result to be retrieved later
   * @param {number} [params.organizationId] Generate report for specific organization.
   * @returns {Promise<GenerateReportApiResponse>}
   */
  generateReport(params: {
    reportId: number;
    deliveryType: ReportDeliveryType;
    organizationId?: number;
    [reportParamName: string]: string | number | boolean | undefined;
  }): Promise<GenerateReportApiResponse> {
    return this.dal.get('generate', {
      params: params,
    });
  }

  /**
   * Get report executions.
   * See {@link https://iotadmins.docs.apiary.io/#reference/reports/report-execution-data/get-report-execution-data}
   *
   * @param params Requested parameters.
   * @param {string|number} params.startDate Mandatory start date.
   * @param {string|number} params.endDate Mandatory end date.
   * @param {number} [params.reportId] Report ID.
   * @param {number} [params.reportGroupId] Report group ID.
   * @param {number} [params.organizationId] Organization ID, required for organizational report group.
   * @param {number} [params.collectionId] Required if reportId and reportGroupId are not provided.
   * @param {number} [params.demandUserId] ID of the user, who requested the report execution.
   * @param {string} [params.sortCollection] Field name to sort report executions collection by.
   * @param {string} [params.sortBy] Field name to sort by.
   * @param {'asc'|'desc'} [params.sortOrder] Sort order, default is ascending.
   * @returns {Promise<GetReportExecutionsApiResponse>}
   */
  getReportExecutionData(params: {
    startDate: string | number;
    endDate: string | number;
    reportId?: number;
    reportGroupId?: number;
    organizationId?: number;
    collectionId?: number;
    demandUserId?: number;
    sortCollection?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<GetReportExecutionsApiResponse> {
    return this.dal.get('data', {params: params});
  }

  /**
   * Get report data.
   * See {@link https://sboxall.peoplepowerco.com/cloud/apidocs/admin.html#tag/Reports/operation/Get%20Report%20Data}
   *
   * @param {string} token JWT generated during report generation.
   * @param [params] Requested parameters.
   * @param {ReportResponseFormat} [params.responseFormat] Report data response format.
   * @returns {Promise<ArrayBuffer|string|Object>}
   */
  getReportData(token: string, params?: { responseFormat?: ReportResponseFormat }): Promise<ArrayBuffer | string | object> {
    const contentType: ResponseType = 'blob';
    return this.dal.get(`data/${encodeURIComponent(token.toString())}`, {
      responseType: contentType,
      params: params,
    });
  }

  /**
   * Get system status.
   * See {@link https://iotadmins.docs.apiary.io/#reference/status/system-status/get-system-status}
   *
   * @param params Requested parameters.
   * @param {number} [params.organizationId] Optional filter by organization ID.
   * @returns {Promise<GetSystemStatusApiResponse>}
   */
  getSystemStatus(params?: {
    organizationId?: number;
  }): Promise<GetSystemStatusApiResponse> {
    return this.adminDal.get('status', {
      params: params,
    });
  }

  /**
   * Get report collections.
   * Returns report collections for an organization.
   * See {@link https://sboxall.peoplepowerco.com/cloud/apidocs/admin.html#tag/Reports/operation/Get%20Report%20Collections}
   *
   * @param {number} organizationId Mandatory organization ID.
   * @param params Requested parameters.
   * @param {number} [params.collectionId] Optional filter by report collection ID.
   * @param {number} [params.reportId] Optional filter by report ID.
   * @returns {Promise<GetReportCollectionsApiResponse>}
   */
  getReportCollections(organizationId: number, params?: {
    collectionId?: number;
    reportId?: number;
  }): Promise<GetReportCollectionsApiResponse> {
    return this.dal.get(`${encodeURIComponent(organizationId.toString())}/collections`, {
      params: params,
    });
  }

  /**
   * Create a new report collection.
   * The collection name and execution schedule are required. The execution schedule is a cron expression.
   * Optionally, a list of reports can be included. Each report must have a reportId. The combination of reportId and parameters must be unique across the collection.
   * See {@link https://sboxall.peoplepowerco.com/cloud/apidocs/admin.html#tag/Reports/operation/Create%20Report%20Collection}
   *
   * @param {number} organizationId Mandatory organization ID.
   * @param {CreateReportCollectionModel} model Model for creating the report collection.
   * @returns {Promise<CreateReportCollectionApiResponse>}
   */
  createReportCollection(organizationId: number, model: CreateReportCollectionModel): Promise<CreateReportCollectionApiResponse> {
    return this.dal.post(`${encodeURIComponent(organizationId.toString())}/collections`, model);
  }

  /**
   * Update an existing report collection.
   * All request body fields are optional. Fields that are not provided retain their existing values.
   * See {@link https://sboxall.peoplepowerco.com/cloud/apidocs/admin.html#tag/Reports/operation/Update%20Report%20Collection}
   *
   * @param {number} organizationId Mandatory organization ID.
   * @param {number} collectionId Mandatory report collection ID.
   * @param {UpdateReportCollectionModel} model Model for updating the report collection.
   * @returns {Promise<UpdateReportCollectionApiResponse>}
   */
  updateReportCollection(organizationId: number, collectionId: number, model: UpdateReportCollectionModel): Promise<UpdateReportCollectionApiResponse> {
    return this.dal.put(`${encodeURIComponent(organizationId.toString())}/collections/${encodeURIComponent(collectionId.toString())}`, model);
  }

  /**
   * Delete a report collection.
   * See {@link https://sboxall.peoplepowerco.com/cloud/apidocs/admin.html#tag/Reports/operation/Delete%20Report%20Collection}
   *
   * @param {number} organizationId Mandatory organization ID.
   * @param {number} collectionId Mandatory report collection ID.
   * @returns {Promise<ApiResponseBase>}
   */
  deleteReportCollection(organizationId: number, collectionId: number): Promise<ApiResponseBase> {
    return this.dal.delete(`${encodeURIComponent(organizationId.toString())}/collections/${encodeURIComponent(collectionId.toString())}`);
  }

}

export interface GenerateReportApiResponse extends ApiResponseBase {
  token: string;
}
